<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick, type PropType } from 'vue';
import {
  DxDataGrid,
  DxColumn,
  DxSorting,
  DxScrolling,
  DxButton as DxGridButton,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import type CustomStore from 'devextreme/data/custom_store';
import type { EntityConfig } from './types';
import EntityEditPopup from './EntityEditPopup.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import { bottoGridPaging } from '../config/grid';
import { showError, showSuccess } from '../api/notify';

const props = defineProps({
  config: { type: Object as PropType<EntityConfig>, required: true },
  store: { type: Object as PropType<CustomStore<any>>, required: true },
  /** Nested under an outer BottoPagePanel / TabPanel — no padding, no duplicate title. */
  embedded: { type: Boolean, default: false },
  onRowDblClick: {
    type: Function as unknown as PropType<((row: any) => void) | null>,
    default: null,
  },
});

const emit = defineEmits<{
  (e: 'created', item: any): void;
  (e: 'updated', item: any): void;
  (e: 'deleted', id: number | string): void;
}>();

const grid = ref<InstanceType<typeof DxDataGrid> | null>(null);

const popupOpen = ref(false);
const popupMode = ref<'create' | 'edit'>('create');
const popupModel = ref<any>(null);

const confirmOpen = ref(false);
const confirmTarget = ref<any>(null);

const { paging, pager } = bottoGridPaging();

const keyExpr = computed(() => props.config.keyExpr ?? 'id');

function openCreate() {
  popupMode.value = 'create';
  popupModel.value = {};
  popupOpen.value = true;
}

function openEdit(row: any) {
  popupMode.value = 'edit';
  popupModel.value = JSON.parse(JSON.stringify(row));
  popupOpen.value = true;
}

function refresh() {
  const instance = grid.value?.instance;
  if (instance) instance.refresh();
}

async function handleSave(payload: any) {
  try {
    if (popupMode.value === 'create') {
      const inserted = await props.store.insert(payload);
      showSuccess('Создано');
      emit('created', inserted);
    } else {
      const key = payload[keyExpr.value];
      const { [keyExpr.value]: _omit, ...rest } = payload;
      const updated = await props.store.update(key, rest);
      showSuccess('Сохранено');
      emit('updated', updated);
    }
    popupOpen.value = false;
    refresh();
  } catch (e) {
    showError(e);
    throw e;
  }
}

function confirmDelete(row: any) {
  confirmTarget.value = row;
  confirmOpen.value = true;
}

async function doDelete() {
  if (!confirmTarget.value) return;
  try {
    const key = confirmTarget.value[keyExpr.value];
    await props.store.remove(key);
    showSuccess('Удалено');
    emit('deleted', key);
    confirmOpen.value = false;
    confirmTarget.value = null;
    refresh();
  } catch (e) {
    showError(e);
  }
}

function handleRowDblClick(e: any) {
  if (props.onRowDblClick) props.onRowDblClick(e.data);
  else openEdit(e.data);
}

function updateGridDimensions() {
  grid.value?.instance?.updateDimensions();
}

const rootEl = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;

onMounted(async () => {
  await nextTick();
  updateGridDimensions();
  window.addEventListener('resize', updateGridDimensions);
  if (rootEl.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => updateGridDimensions());
    resizeObserver.observe(rootEl.value);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateGridDimensions);
  resizeObserver?.disconnect();
  resizeObserver = null;
});

defineExpose({ refresh, openCreate, openEdit, updateGridDimensions });
</script>

<template>
  <div
    ref="rootEl"
    class="entity-list-page"
    :class="{ 'entity-list-page--embedded': embedded }"
  >
    <div :class="embedded ? 'entity-list-page__body' : 'botto-panel'">
      <div class="botto-panel__header">
        <h2 v-if="!embedded" class="botto-panel__title">
          {{ config.titlePlural ?? config.title }}
        </h2>
        <div class="botto-panel__actions">
          <slot name="toolbar-left" />
          <DxButton
            v-if="!config.noCreate"
            icon="add"
            text="Добавить"
            type="default"
            styling-mode="contained"
            @click="openCreate"
          />
          <DxButton icon="refresh" text="Обновить" styling-mode="outlined" @click="refresh" />
          <slot name="toolbar-right" />
        </div>
      </div>

      <div :class="embedded ? 'entity-list-page__content' : 'botto-panel__content'">
        <DxDataGrid
          ref="grid"
          class="entity-list-grid"
          :data-source="store"
          :key-expr="keyExpr"
          :remote-operations="{ paging: true, sorting: true, filtering: false }"
          :show-borders="false"
          :show-row-lines="false"
          :show-column-lines="false"
          :row-alternation-enabled="true"
          :hover-state-enabled="true"
          :column-auto-width="true"
          :allow-column-resizing="true"
          :word-wrap-enabled="false"
          :paging="paging"
          :pager="pager"
          height="100%"
          width="100%"
          @row-dbl-click="handleRowDblClick"
        >
          <DxColumn
            v-for="col in config.columns"
            :key="col.dataField"
            :data-field="col.dataField"
            :caption="col.caption ?? col.dataField"
            :data-type="col.dataType ?? 'string'"
            :width="col.width"
            :visible="col.visible !== false"
            :allow-sorting="col.allowSorting !== false"
            :allow-filtering="col.allowFiltering ?? false"
            :format="(col.format as any)"
            :calculate-display-value="col.calculateDisplayValue"
            :cell-template="col.cellTemplate as any"
          />

          <DxColumn type="buttons" :width="100" caption="Действия">
            <DxGridButton hint="Редактировать" icon="edit" :on-click="(e: any) => openEdit(e.row.data)" />
            <DxGridButton
              v-if="!config.noDelete"
              hint="Удалить"
              icon="trash"
              :on-click="(e: any) => confirmDelete(e.row.data)"
            />
          </DxColumn>

          <DxSorting mode="single" />
          <DxScrolling mode="standard" />
        </DxDataGrid>
      </div>
    </div>

    <EntityEditPopup
      v-if="popupOpen"
      v-model:visible="popupOpen"
      :mode="popupMode"
      :config="config"
      :initial-value="popupModel"
      @save="handleSave"
    />

    <ConfirmDialog
      v-if="confirmOpen"
      v-model:visible="confirmOpen"
      title="Удалить запись?"
      message="Действие необратимо."
      ok-text="Удалить"
      ok-type="danger"
      @ok="doDelete"
    />
  </div>
</template>

<style scoped>
.entity-list-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  overflow: hidden;
}

.entity-list-page--embedded {
  padding: 0;
}

.entity-list-page--embedded .botto-panel__header {
  justify-content: flex-end;
}

.entity-list-page__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  gap: 12px;
}

.entity-list-page__content {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.entity-list-page__content > .dx-datagrid {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
}
</style>
